import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import orangeSortIcon from "./../media/orange-sort-icon.svg";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import "./../styleSheet/main/admin/hr/documents/addDirective/addDirective.css";
import deleteButtonIcon from "./../media/delete-button.svg";
import docButton from "./../media/doc-orange.svg";
import jpgPhotoButton from "./../media/jpg-photo.svg";
import pdfButton from "./../media/orange-PDF.svg";
import txtButton from "./../media/txt-orange.svg";
import xlsButton from "./../media/xls-orange.svg";
import zipButton from "./..//media/zip.svg";
import UploadProgressBar from "./progress/UploadProgressBar";
import JSZip from "jszip";
import Logger from "../utils/Logger";
import { useNotification } from "./NotificationContext";
import httpClient from "../services/httpClient";
import RichTextEditor from "./richTextEditor/richTextEditor";
import { LoadingComponent } from "../App";

function EditDocument() {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [Icon, setIcon] = useState("");
  const [files, setFiles] = useState([]);
  const [originalData, setOriginalData] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [hover, setHover] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { id, category: categoryFromParams } = useParams();

  // Determine category - if not in params, check if it's AI route
  const category =
    categoryFromParams ||
    (location.pathname.includes("/pliki/ai/") ? "ai" : "regulaminy");

  // const { showNotification } = useNotification();

  // Get document type configuration
  const getDocumentConfig = (category) => {
    const configs = {
      // Documents
      regulaminy: {
        title: t("documentEditor.editRegulation"),
        backUrl: "/administrator/pliki/dokumenty/regulaminy",
        subCategory: "regulations",
      },
      ppk: {
        title: t("documentEditor.editPPK"),
        backUrl: "/administrator/pliki/dokumenty/ppk",
        subCategory: "ppk",
      },
      zarzadzenia: {
        title: t("documentEditor.editDirective"),
        backUrl: "/administrator/pliki/dokumenty/zarzadzenia",
        subCategory: "directives",
      },
      dyrektywy: {
        title: t("documentEditor.editDirective"),
        backUrl: "/administrator/pliki/dokumenty/zarzadzenia",
        subCategory: "directives",
      },
      druki: {
        title: t("documentEditor.editPrint"),
        backUrl: "/administrator/pliki/dokumenty/druki",
        subCategory: "prints",
      },
      instructions: {
        title: t("documentEditor.editInstruction"),
        backUrl: "/administrator/pliki/dokumenty/instrukcje",
        subCategory: "instructions",
      },
      // Benefits
      multisport: {
        title: t("documentEditor.editMultisport"),
        backUrl: "/administrator/pliki/benefity/multisport",
        subCategory: "multisport",
      },
      "opieka-medyczna": {
        title: t("documentEditor.editMedicalCare"),
        backUrl: "/administrator/pliki/benefity/opieka-medyczna",
        subCategory: "medicalcare",
      },
      "ubezpieczenie-grupowe": {
        title: t("documentEditor.editGroupInsurance"),
        backUrl: "/administrator/pliki/benefity/ubezpieczenie-grupowe",
        subCategory: "groupinsurance",
      },
      // AI
      ai: {
        title: t("documentEditor.editAI"),
        backUrl: "/administrator/pliki/ai",
        subCategory: "ai",
      },
      // PPWR
      ppwr: {
        title: t("documentEditor.editPPWR"),
        backUrl: "/administrator/pliki/ppwr",
        subCategory: "ppwr",
      },
      raporty: {
        title: t("documentEditor.editReport"),
        backUrl: "/administrator/pliki/dokumenty/raporty",
        subCategory: "reports",
      },
    };
    return configs[category] || configs.regulaminy;
  };

  const config = getDocumentConfig(category);

  // Fetch existing document data
  useEffect(() => {
    const fetchDocument = async () => {
      try {
        setIsLoadingData(true);
        const response = await httpClient.get(`hrdocuments/${id}`, {
          withAuth: true,
        });

        const documentData = response.data;
        setOriginalData(documentData);
        setTitle(documentData.title || "");
        setDescription(documentData.description || "");
        setIcon(documentData.icon || "");

        Logger.log("Loaded document data:", documentData);
      } catch (error) {
        Logger.error("Error fetching document:", error);
        // showNotification("Nie udało się załadować dokumentu.", "error");
        navigate(config.backUrl);
      } finally {
        setIsLoadingData(false);
      }
    };

    if (id) {
      fetchDocument();
    }
  }, [id, navigate, config.backUrl]);

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    Logger.log("Wybrane pliki:", newFiles);
  };

  const handleRemoveFile = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
  };

  const getIconWithPathForFile = (fileName) => {
    if (!fileName) return null;
    const fileExtension = fileName.split(".").pop().toLowerCase();
    switch (fileExtension) {
      case "jpg":
      case "jpeg":
      case "png":
      case "svg":
        return jpgPhotoButton;
      case "pdf":
        return pdfButton;
      case "doc":
      case "docx":
        return docButton;
      case "xls":
      case "xlsx":
        return xlsButton;
      case "txt":
        return txtButton;
      case "zip":
        return zipButton;
      default:
        return txtButton;
    }
  };

  const getIconForFile = (fileName) => {
    if (!fileName) return "txtButton";
    const fileExtension = fileName.split(".").pop().toLowerCase();
    switch (fileExtension) {
      case "jpg":
      case "jpeg":
      case "png":
      case "svg":
        return "jpgPhotoButton";
      case "pdf":
        return "pdfButton";
      case "doc":
      case "docx":
        return "docButton";
      case "xls":
      case "xlsx":
        return "xlsButton";
      case "txt":
        return "txtButton";
      case "zip":
        return "zipButton";
      default:
        return "txtButton";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title) {
      // showNotification("Tytuł jest wymagany!", "error");
      return;
    }

    setIsLoading(true);

    const formData = new FormData();
    formData.append("title", title);
    formData.append("name", title);
    formData.append("description", description);

    // Determine category based on the route
    const isBenefits =
      category.includes("multisport") ||
      category.includes("opieka-medyczna") ||
      category.includes("ubezpieczenie-grupowe");
    const isAI = category === "ai";
    const isPPWR = category === "ppwr";

    let mainCategory = "documents";
    if (isBenefits) {
      mainCategory = "benefits";
    } else if (isAI) {
      mainCategory = "ai";
    } else if (isPPWR) {
      mainCategory = "ppwr";
    }

    formData.append("category", mainCategory);
    formData.append("subCategory", config.subCategory);

    // Only include file if new files were selected
    if (files.length > 0) {
      if (files.length > 1) {
        const zip = new JSZip();
        files.forEach((file) => zip.file(file.name, file));
        const zipFileName = `dokumenty-${uuidv4()}.zip`;
        const zipBlob = await zip.generateAsync({ type: "blob" });
        const zipFile = new File([zipBlob], zipFileName, {
          type: "application/zip",
        });
        formData.append("file", zipFile);
        formData.append("icon", "zipButton");
      } else {
        formData.append("file", files[0]);
        const iconName = getIconForFile(files[0].name);
        formData.append("icon", iconName);
      }
    } else {
      formData.append("icon", Icon);
    }

    try {
      console.log(formData);
      await httpClient.put(`/hrdocuments/${id}`, formData, {
        withAuth: true,
        config: {
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            setUploadProgress(progress);
          },
        },
      });

      setUploadProgress(0);
      setSuccessMessage(t("documentEditor.successUpdate"));
      // showNotification(t("documentEditor.successUpdate"), "success");

      setTimeout(() => {
        navigate(config.backUrl);
      }, 500);
    } catch (error) {
      Logger.error("Błąd:", error);
      setUploadProgress(0);
      // showNotification(
      //   t("documentEditor.errorUpdateFailed"),
      //   "error"
      // );
    } finally {
      setIsLoading(false);
    }
  };

  const handleIconClick = () => {
    navigate(config.backUrl);
  };

  const globalIcon =
    files.length > 1
      ? zipButton
      : files.length === 1
        ? getIconWithPathForFile(files[0].name)
        : originalData?.fields?.icon
          ? getIconWithPathForFile(originalData.fields.icon)
          : null;

  if (isLoadingData) {
    return <LoadingComponent />;
  }

  return (
    <div className="add-announcement-container">
      <div className="main-site">
        <h1>{config.title} :</h1>
        <div
          className="orange-sort-icon"
          onClick={handleIconClick}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            backgroundImage: `url(${orangeSortIcon})`,
            transform: hover ? "rotate(180deg)" : "rotate(90deg)",
            cursor: "pointer",
          }}
        />
      </div>
      <form onSubmit={handleSubmit} className="add-announcement-form">
        <div className="form-group">
          <label htmlFor="title">{t("documentEditor.documentTitle")}</label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("documentEditor.enterTitle")}
          />
        </div>
        <div className="form-group">
          <label htmlFor="description">
            {t("documentEditor.documentDescription")}
          </label>
          <RichTextEditor content={description} onChange={setDescription} />
        </div>
        <div className="form-group">
          <label htmlFor="files">
            {t("documentEditor.selectDocuments")} ({t("common.optional")}):
          </label>
          <label htmlFor="files" className="custom-file-upload">
            {t("documentEditor.addFiles")}
          </label>
          <input
            type="file"
            id="files"
            multiple
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          {originalData?.fields?.filePath && files.length === 0 && (
            <p className="current-file-info">
              {t("common.currentFile")}:{" "}
              {originalData.fields.fileName || t("common.document")}
            </p>
          )}
        </div>
        {files.length > 0 && (
          <div className="selected-files">
            <h4>{t("common.newAttachedFiles")}:</h4>
            <div className="flex-section">
              <div className="global-icon">
                <img
                  src={globalIcon}
                  alt="Globalna ikona pliku"
                  className="file-icon"
                />
              </div>
              <ul>
                {files.map((file, index) => (
                  <li key={index} className="file-item">
                    <span>{file.name}</span>
                    <button
                      type="button"
                      className="remove-file"
                      onClick={() => handleRemoveFile(index)}
                    >
                      <img
                        src={deleteButtonIcon}
                        alt={t("documentEditor.remove")}
                      />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {isLoading && <UploadProgressBar progress={uploadProgress} />}

        <button type="submit" className="submit-button" disabled={isLoading}>
          {t("documentEditor.updateButton")}
        </button>
      </form>

      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}
    </div>
  );
}

export default EditDocument;
